import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity } from '../../utils/security';
import {
    ArrowLeft, Building2, Calculator, BarChart3, TrendingUp,
    FileText, Download, Printer, RefreshCw, ExternalLink,
    Sliders, Globe, Layers, DollarSign,
    Search, ChevronRight, ArrowUpRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

// Municipal apps catalogue with default pricing in GEL (₾)
const DEFAULT_APPS = [
    {
        id: 'portal',
        name_ka: 'მუნიციპალური ოფიციალური პორტალი & CMS',
        name_en: 'Official Municipal Portal & CMS',
        category: 'core',
        category_label: 'ძირითადი პლატფორმა',
        description: 'WCAG 2.1 AA მისაწვდომობა, სახელმწიფო სტანდარტების დიზაინი, მრავალენოვანი CMS, მობილური ადაპტაცია.',
        setup_price: 18000,
        annual_support: 6000,
        badge_color: '#2563eb',
        icon: '🏛️'
    },
    {
        id: 'dms',
        name_ka: 'ელ-კანცელარია & დოკუმენტბრუნვა (DMS)',
        name_en: 'E-Chancery & Document Management (DMS)',
        category: 'governance',
        category_label: 'შიდა მმართველობა',
        description: 'ციფრული ხელმოწერა, შიდა ბრძანებები, ტიკეტები, განცხადებების აღრიცხვა და ვადების ავტომატური მონიტორინგი.',
        setup_price: 24000,
        annual_support: 8000,
        badge_color: '#7c3aed',
        icon: '📑'
    },
    {
        id: 'citizen_voice',
        name_ka: 'მოქალაქის ხმა & ელექტრონული პეტიციები',
        name_en: 'Citizen Voice & Public Petitions',
        category: 'engagement',
        category_label: 'მოქალაქეთა ჩართულობა',
        description: 'პრობლემის დაფიქსირება რუკაზე ფოტოთი, ონლაინ პეტიციები, კენჭისყრა, საჯარო დისკუსია და მერის უკუკავშირი.',
        setup_price: 12000,
        annual_support: 4000,
        badge_color: '#059669',
        icon: '🗣️'
    },
    {
        id: 'gis_maps',
        name_ka: 'GIS მუნიციპალური რუკა & ზონირება',
        name_en: 'Municipal GIS Mapping & Zoning',
        category: 'spatial',
        category_label: 'სივრცითი დაგეგმარება',
        description: 'ინტერაქტიული GIS ფენები, მიწის ნაკვეთების ზონირება, ინფრასტრუქტურის ობიექტები და მშენებლობის ნებართვები.',
        setup_price: 22000,
        annual_support: 7500,
        badge_color: '#0891b2',
        icon: '🗺️'
    },
    {
        id: 'smart_tourism',
        name_ka: 'ჭკვიანი ტურიზმის & მემკვიდრეობის აპლიკაცია',
        name_en: 'Smart Tourism & Heritage Guide',
        category: 'tourism',
        category_label: 'ტურიზმი და კულტურა',
        description: 'აუდიოგზამკვლევი, ტურისტული ლოკაციების კატალოგი, QR კოდების სისტემა, მუზეუმები და ღონისძიებების აფიშა.',
        setup_price: 14000,
        annual_support: 4500,
        badge_color: '#d97706',
        icon: '🏰'
    },
    {
        id: 'emergency_alerts',
        name_ka: 'საგანგებო შეტყობინებების & SMS/Push ცენტრი',
        name_en: 'Emergency Broadcast & SMS Alerts',
        category: 'safety',
        category_label: 'უსაფრთხოება',
        description: 'მყისიერი კრიზისული შეტყობინებები, წყალმომარაგების/გზების შეზღუდვების SMS გაგზავნა მიზნობრივ უბნებზე.',
        setup_price: 9500,
        annual_support: 3500,
        badge_color: '#dc2626',
        icon: '🚨'
    },
    {
        id: 'open_budget',
        name_ka: 'ღია ბიუჯეტი & ფინანსური გამჭვირვალობა',
        name_en: 'Open Budget & Citizen Finance',
        category: 'transparency',
        category_label: 'გამჭვირვალობა',
        description: 'მუნიციპალური ბიუჯეტის ინფოგრაფიკული ვიზუალიზაცია, ხარჯების მონიტორინგი და მონაწილეობითი ბიუჯეტირება.',
        setup_price: 11000,
        annual_support: 3800,
        badge_color: '#4f46e5',
        icon: '📊'
    },
    {
        id: 'mobile_app',
        name_ka: 'მუნიციპალური მობილური აპლიკაცია (iOS / Android)',
        name_en: 'Citizen Mobile App (iOS / Android)',
        category: 'mobile',
        category_label: 'მობილური აპლიკაცია',
        description: 'ყველა მუნიციპალური სერვისი ერთ აპლიკაციაში, Push ნოტიფიკაციები, ციფრული საშვები და ონლაინ გადახდები.',
        setup_price: 28000,
        annual_support: 9000,
        badge_color: '#9333ea',
        icon: '📱'
    }
];

// 4 Status Helper
const getStatusBadge = (status) => {
    switch (status) {
        case 'contract_signed':
        case 'supported_live':
            return {
                label: '🟢 კონტრაქტი გაფორმდა',
                bg: '#dcfce7',
                text: '#14532d',
                border: '#86efac'
            };
        case 'negotiation':
        case 'meeting_scheduled':
        case 'in_development':
            return {
                label: '🟡 სამუშაო პროცესში',
                bg: '#fef08a',
                text: '#713f12',
                border: '#fde047'
            };
        case 'offer_sent':
            return {
                label: '🔵 შეთავაზება გაგზავნილია',
                bg: '#dbeafe',
                text: '#1e3a8a',
                border: '#93c5fd'
            };
        default:
            return {
                label: '🔴 ლიდი / კომუნიკაციის გარეშე',
                bg: '#fee2e2',
                text: '#7f1d1d',
                border: '#fca5a5'
            };
    }
};

const AdminMunicipalityReport = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();

    // Data State
    const [municipalities, setMunicipalities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeView, setActiveView] = useState('calculator'); // 'calculator' | 'report'

    // Calculator State
    const [selectedMunIds, setSelectedMunIds] = useState([]); // Array of municipality IDs
    const [apps, setApps] = useState(DEFAULT_APPS); // Synced digital products
    const [selectedAppIds, setSelectedAppIds] = useState(['portal', 'dms', 'citizen_voice', '1', '2', '3']); // Default selected products
    const [bundleDiscount, setBundleDiscount] = useState(10); // % discount for multi-app
    const [searchMuni, setSearchMuni] = useState('');
    const [filterRegion, setFilterRegion] = useState('all');

    // Report Table State
    const [reportSearch, setReportSearch] = useState('');
    const [reportFilterStatus, setReportFilterStatus] = useState('all');

    // Check authorization
    useEffect(() => {
        if (!authLoading && !isSuperAdmin()) {
            navigate('/admin');
        }
    }, [authLoading, isSuperAdmin, navigate]);

    // Fetch municipalities
    const fetchMunicipalities = async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}municipalities.php`);
            const data = await res.json();
            if (Array.isArray(data)) {
                // Filter out occupied municipalities for sales calculator
                const active = data.filter(m => !m.is_occupied && !['akhalgori', 'azhara', 'eredvi', 'kurta', 'tighva'].includes(m.slug));
                setMunicipalities(active);

                // Default selection: Akhaltsikhe as specifically requested in user prompt
                const akhaltsikhe = active.find(m => m.slug === 'akhaltsikhe' || (m.name_ka && m.name_ka.includes('ახალციხე')));
                if (akhaltsikhe) {
                    setSelectedMunIds([akhaltsikhe.id]);
                } else if (active.length > 0) {
                    setSelectedMunIds([active[0].id]);
                }
            }
        } catch (err) {
            console.error('Error fetching municipalities:', err);
        }
        setLoading(false);
    };

    // Fetch and sync live digital products from database
    const fetchDigitalProducts = async () => {
        try {
            const res = await fetch(`${API_URL}products.php`);
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
                const synced = data.map(p => ({
                    id: String(p.id),
                    slug: p.slug,
                    name_ka: p.name_ka || p.name_en,
                    name_en: p.name_en || p.name_ka,
                    category: p.category_name_en || 'core',
                    category_label: p.category_name_ka || p.category_name_en || 'ციფრული პლატფორმა',
                    description: p.tagline_ka || p.description_ka || p.tagline_en || p.description_en || '',
                    setup_price: parseFloat(p.setup_price) || 15000,
                    annual_support: parseFloat(p.annual_price || p.annual_support) || 5000,
                    badge_color: p.badge_color || '#6366f1',
                    icon: p.icon || '📦'
                }));
                setApps(synced);
                // Ensure default selections map to live products
                setSelectedAppIds(prev => {
                    const matching = prev.filter(id => synced.some(a => a.id === id || a.slug === id));
                    if (matching.length > 0) {
                        return matching.map(id => {
                            const found = synced.find(a => a.id === id || a.slug === id);
                            return found ? found.id : id;
                        });
                    }
                    return synced.slice(0, 3).map(a => a.id);
                });
            }
        } catch (err) {
            console.error('Error syncing digital products:', err);
        }
    };

    useEffect(() => {
        if (user) {
            fetchMunicipalities();
            fetchDigitalProducts();
        }
    }, [user]);

    // Regions list for filtering
    const regions = useMemo(() => {
        const set = new Set();
        municipalities.forEach(m => {
            if (m.region_ka) set.add(m.region_ka);
            else if (m.region) set.add(m.region);
        });
        return Array.from(set).sort();
    }, [municipalities]);

    // Selected Municipalities Objects
    const selectedMunicipalities = useMemo(() => {
        return municipalities.filter(m => selectedMunIds.includes(m.id));
    }, [municipalities, selectedMunIds]);

    // Selected Apps Objects (Synced with live digital products)
    const selectedApps = useMemo(() => {
        return apps.filter(app => selectedAppIds.includes(app.id) || selectedAppIds.includes(app.slug));
    }, [apps, selectedAppIds]);

    // Quick presets
    const handlePresetSelect = (preset) => {
        if (preset === 'akhaltsikhe') {
            const m = municipalities.find(item => item.slug === 'akhaltsikhe' || (item.name_ka && item.name_ka.includes('ახალციხე')));
            if (m) setSelectedMunIds([m.id]);
        } else if (preset === 'samtskhe') {
            const samtskhe = municipalities.filter(item => 
                (item.region_ka && item.region_ka.includes('სამცხე-ჯავახეთი')) || 
                (item.region && item.region.toLowerCase().includes('samtskhe'))
            );
            setSelectedMunIds(samtskhe.map(m => m.id));
        } else if (preset === 'big5') {
            const big5 = municipalities.filter(item => 
                ['tbilisi', 'batumi', 'kutaisi', 'rustavi', 'poti'].includes(item.slug) ||
                (item.name_ka && ['თბილისი', 'ბათუმი', 'ქუთაისი', 'რუსთავი', 'ფოთი'].some(n => item.name_ka.includes(n)))
            );
            setSelectedMunIds(big5.map(m => m.id));
        } else if (preset === 'regional_centers') {
            const centers = ['tbilisi', 'batumi', 'kutaisi', 'rustavi', 'telavi', 'gori', 'zugdidi', 'akhaltsikhe', 'ambrolauri', 'ozurgeti'];
            const found = municipalities.filter(item => centers.includes(item.slug));
            setSelectedMunIds(found.map(m => m.id));
        } else if (preset === 'all') {
            setSelectedMunIds(municipalities.map(m => m.id));
        } else if (preset === 'clear') {
            setSelectedMunIds([]);
        }
    };

    // Toggle municipality
    const toggleMunicipality = (id) => {
        setSelectedMunIds(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // Toggle app
    const toggleApp = (id) => {
        setSelectedAppIds(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    // ── CALCULATION ENGINE ──
    const calculations = useMemo(() => {
        const muniCount = selectedMunicipalities.length;
        const appCount = selectedApps.length;

        // Base single-muni package cost
        const rawPackageSetup = selectedApps.reduce((acc, app) => acc + app.setup_price, 0);
        const rawPackageAnnual = selectedApps.reduce((acc, app) => acc + app.annual_support, 0);

        // Apply bundle discount if multiple apps selected
        const discountFactor = (100 - bundleDiscount) / 100;
        const discountedPackageSetup = Math.round(rawPackageSetup * discountFactor);
        const discountedPackageAnnual = Math.round(rawPackageAnnual * discountFactor);

        // Grand totals across all selected municipalities
        const totalSetupIncome = discountedPackageSetup * muniCount;
        const totalAnnualSupport = discountedPackageAnnual * muniCount;
        const totalMonthlySupport = Math.round(totalAnnualSupport / 12);

        // 3-Year Contract Total Value (Setup + 3 Years ARR)
        const total3YearValue = totalSetupIncome + (totalAnnualSupport * 3);
        // 5-Year LTV
        const total5YearValue = totalSetupIncome + (totalAnnualSupport * 5);

        // App-level breakdown
        const appBreakdown = selectedApps.map(app => {
            const appSetupTotal = Math.round(app.setup_price * discountFactor * muniCount);
            const appAnnualTotal = Math.round(app.annual_support * discountFactor * muniCount);
            const app3YrTotal = appSetupTotal + (appAnnualTotal * 3);
            return {
                ...app,
                appSetupTotal,
                appAnnualTotal,
                app3YrTotal,
                percentOfTotal: total3YearValue > 0 ? ((app3YrTotal / total3YearValue) * 100).toFixed(1) : 0
            };
        });

        // Municipality-level breakdown
        const muniBreakdown = selectedMunicipalities.map(m => {
            const muniSetup = discountedPackageSetup;
            const muniAnnual = discountedPackageAnnual;
            const muni3Yr = muniSetup + (muniAnnual * 3);
            return {
                id: m.id,
                name_ka: m.name_ka,
                name_en: m.name_en,
                region: m.region_ka || m.region,
                logo: m.logo,
                status: m.status,
                setup: muniSetup,
                annual: muniAnnual,
                total3Yr: muni3Yr
            };
        });

        // Timeline milestones for charts
        const timeline = [
            { label: 'საწყისი ეტაპი (ინტეგრაცია)', month: 0, revenue: totalSetupIncome },
            { label: '1-ლი წელი (სრული შემოსავალი)', month: 12, revenue: totalSetupIncome + totalAnnualSupport },
            { label: 'მე-2 წელი (კუმულაციური)', month: 24, revenue: totalSetupIncome + (totalAnnualSupport * 2) },
            { label: 'მე-3 წელი (საბოლოო 3-წლიანი)', month: 36, revenue: total3YearValue },
            { label: 'მე-5 წელი (LTV პოტენციალი)', month: 60, revenue: total5YearValue }
        ];

        return {
            muniCount,
            appCount,
            rawPackageSetup,
            rawPackageAnnual,
            discountedPackageSetup,
            discountedPackageAnnual,
            totalSetupIncome,
            totalAnnualSupport,
            totalMonthlySupport,
            total3YearValue,
            total5YearValue,
            appBreakdown,
            muniBreakdown,
            timeline
        };
    }, [selectedMunicipalities, selectedApps, bundleDiscount]);

    // Filtered Municipalities for selector modal/drawer
    const filteredMunicipalities = useMemo(() => {
        return municipalities.filter(m => {
            const matchSearch = !searchMuni.trim() || 
                (m.name_ka && m.name_ka.toLowerCase().includes(searchMuni.toLowerCase())) ||
                (m.name_en && m.name_en.toLowerCase().includes(searchMuni.toLowerCase())) ||
                (m.slug && m.slug.toLowerCase().includes(searchMuni.toLowerCase()));
            const matchRegion = filterRegion === 'all' || (m.region_ka === filterRegion || m.region === filterRegion);
            return matchSearch && matchRegion;
        });
    }, [municipalities, searchMuni, filterRegion]);

    // Filtered Municipalities for Full Report Tab
    const reportMunicipalities = useMemo(() => {
        return municipalities.filter(m => {
            const matchSearch = !reportSearch.trim() || 
                (m.name_ka && m.name_ka.toLowerCase().includes(reportSearch.toLowerCase())) ||
                (m.name_en && m.name_en.toLowerCase().includes(reportSearch.toLowerCase())) ||
                (m.slug && m.slug.toLowerCase().includes(reportSearch.toLowerCase()));
            
            let matchStatus = true;
            if (reportFilterStatus === 'green') {
                matchStatus = ['contract_signed', 'supported_live'].includes(m.status);
            } else if (reportFilterStatus === 'yellow') {
                matchStatus = ['negotiation', 'meeting_scheduled', 'in_development'].includes(m.status);
            } else if (reportFilterStatus === 'blue') {
                matchStatus = m.status === 'offer_sent';
            } else if (reportFilterStatus === 'red') {
                matchStatus = ['lead', 'rejected'].includes(m.status);
            }
            return matchSearch && matchStatus;
        });
    }, [municipalities, reportSearch, reportFilterStatus]);

    // CSV Export function
    const exportCSV = () => {
        const headers = ['მუნიციპალიტეტი,რეგიონი,არჩეული აპლიკაციები,საწყისი ლიცენზია (₾),წლიური მხარდაჭერა (₾),3-წლიანი ჯამური ღირებულება (₾)'];
        const appNames = selectedApps.map(a => a.name_ka).join(' + ');
        const rows = calculations.muniBreakdown.map(m => 
            `"${m.name_ka}","${m.region || ''}","${appNames}",${m.setup},${m.annual},${m.total3Yr}`
        );
        const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers, ...rows].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `orbitronix_municipal_revenue_projection_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <AdminLayout title="მუნიციპალიტეტების სრული ანგარიში და კალკულატორი">
            {/* ── TOP HEADER & NAVIGATION BAR ── */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <button 
                        className="shadcn-btn shadcn-btn-secondary" 
                        onClick={() => navigate('/admin/municipalities')}
                    >
                        <ArrowLeft size={16} /> მუნიციპალიტეტების სია
                    </button>
                    <div>
                        <h4 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                            <span>მუნიციპალური აპლიკაციების შემოსავლების კალკულატორი & ანგარიში</span>
                            <span className="badge rounded-pill fs-7 px-3 py-1 text-white" style={{ background: '#6366f1' }}>
                                64 მუნიციპალიტეტი
                            </span>
                        </h4>
                        <span className="text-muted smallest">
                            შეარჩიეთ მუნიციპალიტეტი და პროგრამული პროდუქტები სავარაუდო შემოსავლის, ARR-ისა და კონტრაქტის გრაფიკული პროგნოზისთვის
                        </span>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchMunicipalities} disabled={loading}>
                        <RefreshCw size={14} className={loading ? 'fa-spin' : ''} /> {loading ? 'იტვირთება...' : 'განახლება'}
                    </button>
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={exportCSV} disabled={selectedMunicipalities.length === 0}>
                        <Download size={14} /> CSV ექსპორტი
                    </button>
                    <button className="shadcn-btn shadcn-btn-primary shadcn-btn-sm" onClick={() => window.print()}>
                        <Printer size={14} /> ბეჭდვა / PDF
                    </button>
                </div>
            </div>

            {/* ── TOP SWITCH TABS: CALCULATOR vs FULL REPORT ── */}
            <div className="d-flex gap-2 mb-4 border-bottom pb-2">
                <button
                    className={`shadcn-btn ${activeView === 'calculator' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                    onClick={() => setActiveView('calculator')}
                >
                    <Calculator size={16} />
                    <span>აპლიკაციების შემოსავლის კალკულატორი</span>
                    {selectedMunicipalities.length > 0 && (
                        <span className="badge bg-white text-dark rounded-pill ms-1">
                            {selectedMunicipalities.length} მუნიციპ. / {selectedApps.length} აპი
                        </span>
                    )}
                </button>
                <button
                    className={`shadcn-btn ${activeView === 'report' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                    onClick={() => setActiveView('report')}
                >
                    <FileText size={16} />
                    <span>საქართველოს მუნიციპალიტეტების სრული ანგარიში</span>
                    <span className="badge bg-light text-muted rounded-pill ms-1">{municipalities.length}</span>
                </button>
                <button
                    className="shadcn-btn shadcn-btn-secondary ms-auto d-none d-md-flex align-items-center gap-1"
                    onClick={() => navigate('/admin/municipalities/analytics')}
                >
                    <TrendingUp size={16} className="text-indigo" style={{ color: '#6366f1' }} />
                    <span>AI ბაზრის პროგნოზი</span>
                    <ArrowUpRight size={14} />
                </button>
            </div>

            {activeView === 'calculator' ? (
                /* ══════════════════════════════════════════════════════════════════════════════
                   VIEW 1: INTERACTIVE APP REVENUE CALCULATOR
                   ══════════════════════════════════════════════════════════════════════════════ */
                <div>
                    {/* ── SECTION 1: SELECTION CONTROLS (MUNICIPALITIES & APPS) ── */}
                    <div className="row g-4 mb-4">
                        {/* LEFT COLUMN: MUNICIPALITIES SELECTOR */}
                        <div className="col-12 col-xl-6">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="shadcn-card-title d-flex align-items-center gap-2">
                                        <Building2 size={20} className="text-primary" />
                                        <span>1. სამიზნე მუნიციპალიტეტების შერჩევა</span>
                                    </div>
                                    <span className="badge bg-primary bg-opacity-10 text-primary fw-bold px-2 py-1 rounded">
                                        არჩეულია: {selectedMunicipalities.length}
                                    </span>
                                </div>

                                <div className="p-3">
                                    {/* Quick Selection Presets */}
                                    <div className="mb-3">
                                        <span className="smallest text-muted fw-bold d-block mb-1">სწრაფი არჩევა (პრესეტები):</span>
                                        <div className="d-flex flex-wrap gap-1">
                                            <button 
                                                className={`btn btn-sm ${selectedMunIds.length === 1 && selectedMunicipalities[0]?.slug === 'akhaltsikhe' ? 'btn-primary' : 'btn-outline-primary'}`}
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('akhaltsikhe')}
                                            >
                                                🎯 ახალციხე (Akhaltsikhe)
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-secondary" 
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('samtskhe')}
                                            >
                                                🌲 სამცხე-ჯავახეთი (6)
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-secondary" 
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('big5')}
                                            >
                                                🏙️ 5 მსხვილი ქალაქი
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-secondary" 
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('regional_centers')}
                                            >
                                                🏔️ რეგიონული ცენტრები (10)
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-secondary" 
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('all')}
                                            >
                                                🇬🇪 ყველა (64)
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-outline-danger" 
                                                style={{ fontSize: '11px', padding: '3px 8px' }}
                                                onClick={() => handlePresetSelect('clear')}
                                            >
                                                ❌ გასუფთავება
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Municipalities Chips */}
                                    {selectedMunicipalities.length > 0 && (
                                        <div className="mb-3 p-2 rounded bg-light border">
                                            <div className="smallest text-muted fw-bold mb-1">არჩეული მუნიციპალიტეტები:</div>
                                            <div className="d-flex flex-wrap gap-1" style={{ maxHeight: '110px', overflowY: 'auto' }}>
                                                {selectedMunicipalities.map(m => (
                                                    <span 
                                                        key={m.id} 
                                                        className="badge bg-white text-dark border d-flex align-items-center gap-1 py-1 px-2 shadow-xs"
                                                    >
                                                        {m.logo ? (
                                                            <img src={m.logo} alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }} />
                                                        ) : (
                                                            <span>🏛️</span>
                                                        )}
                                                        <span className="fw-semibold">{m.name_ka}</span>
                                                        <button 
                                                            type="button" 
                                                            className="btn-close ms-1" 
                                                            style={{ fontSize: '8px' }}
                                                            onClick={(e) => { e.stopPropagation(); toggleMunicipality(m.id); }}
                                                        />
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Search & Region Filter Bar */}
                                    <div className="row g-2 mb-3">
                                        <div className="col-7">
                                            <div className="position-relative">
                                                <Search size={14} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                                                <input 
                                                    type="text" 
                                                    className="shadcn-input ps-4" 
                                                    placeholder="ძებნა (მაგ: ახალციხე)..."
                                                    value={searchMuni}
                                                    onChange={e => setSearchMuni(e.target.value)}
                                                    style={{ fontSize: '12px', height: '34px' }}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-5">
                                            <select 
                                                className="shadcn-input"
                                                value={filterRegion}
                                                onChange={e => setFilterRegion(e.target.value)}
                                                style={{ fontSize: '12px', height: '34px' }}
                                            >
                                                <option value="all">ყველა რეგიონი</option>
                                                {regions.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Scrollable List of Municipalities */}
                                    <div className="border rounded-3 p-2" style={{ maxHeight: '280px', overflowY: 'auto', background: '#fafafa' }}>
                                        <div className="row g-2">
                                            {filteredMunicipalities.map(m => {
                                                const isSelected = selectedMunIds.includes(m.id);
                                                return (
                                                    <div key={m.id} className="col-12 col-md-6">
                                                        <div 
                                                            className={`p-2 rounded-2 border d-flex align-items-center justify-content-between cursor-pointer transition-all ${isSelected ? 'bg-primary bg-opacity-10 border-primary' : 'bg-white'}`}
                                                            onClick={() => toggleMunicipality(m.id)}
                                                            style={{ cursor: 'pointer', minHeight: '44px' }}
                                                        >
                                                            <div className="d-flex align-items-center gap-2 overflow-hidden">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={isSelected} 
                                                                    onChange={() => {}} 
                                                                    className="form-check-input mt-0 flex-shrink-0"
                                                                />
                                                                {m.logo ? (
                                                                    <img src={m.logo} alt="" style={{ width: '20px', height: '20px', objectFit: 'contain' }} />
                                                                ) : (
                                                                    <span>🏛️</span>
                                                                )}
                                                                <div className="text-truncate">
                                                                    <div className="fw-semibold text-dark small text-truncate">{m.name_ka}</div>
                                                                    <div className="text-muted smallest text-truncate">{m.region_ka || m.region}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: APPS SELECTION */}
                        <div className="col-12 col-xl-6">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="shadcn-card-title d-flex align-items-center gap-2">
                                        <Layers size={20} className="text-indigo" style={{ color: '#6366f1' }} />
                                        <span>2. Orbitronix ციფრული პროდუქტების პაკეტი</span>
                                    </div>
                                    <span className="badge bg-indigo text-white fw-bold px-2 py-1 rounded" style={{ background: '#6366f1' }}>
                                        არჩეულია: {selectedApps.length} / {apps.length} პროდუქტი
                                    </span>
                                </div>

                                <div className="p-3">
                                    {/* Discount slider and package controls */}
                                    <div className="p-2 mb-3 rounded-2 bg-light border d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <div className="d-flex align-items-center gap-2">
                                            <Sliders size={16} className="text-primary" />
                                            <span className="small fw-semibold text-dark">პაკეტური ფასდაკლება (Bundle Discount):</span>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <input 
                                                type="range" 
                                                min="0" 
                                                max="30" 
                                                step="5" 
                                                value={bundleDiscount}
                                                onChange={e => setBundleDiscount(parseInt(e.target.value))}
                                                style={{ width: '100px' }}
                                            />
                                            <span className="badge bg-success rounded-pill fw-bold">-{bundleDiscount}%</span>
                                        </div>
                                    </div>

                                    {/* App Cards List */}
                                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                        {apps.map(app => {
                                            const isSelected = selectedAppIds.includes(app.id);
                                            const discountedSetup = Math.round(app.setup_price * (1 - bundleDiscount / 100));
                                            const discountedAnnual = Math.round(app.annual_support * (1 - bundleDiscount / 100));

                                            return (
                                                <div 
                                                    key={app.id} 
                                                    className={`p-3 rounded-3 border transition-all ${isSelected ? 'border-primary shadow-xs' : 'border-light bg-light opacity-75'}`}
                                                    onClick={() => toggleApp(app.id)}
                                                    style={{ 
                                                        cursor: 'pointer',
                                                        background: isSelected ? '#ffffff' : '#f8fafc',
                                                        borderLeft: isSelected ? `5px solid ${app.badge_color}` : '1px solid #e2e8f0'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-start justify-content-between gap-2">
                                                        <div className="d-flex align-items-start gap-2">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isSelected} 
                                                                onChange={() => {}} 
                                                                className="form-check-input mt-1"
                                                            />
                                                            <span style={{ fontSize: '20px' }}>{app.icon}</span>
                                                            <div>
                                                                <div className="fw-bold text-dark small">{app.name_ka}</div>
                                                                <div className="text-muted smallest">{app.description}</div>
                                                            </div>
                                                        </div>

                                                        {/* App Pricing Badges */}
                                                        <div className="text-end flex-shrink-0">
                                                            <div className="fw-bold text-primary small">
                                                                ₾{discountedSetup.toLocaleString()}
                                                                <span className="text-muted smallest fw-normal d-block">საწყისი ლიცენზია</span>
                                                            </div>
                                                            <div className="fw-semibold text-success smallest mt-1">
                                                                +₾{discountedAnnual.toLocaleString()}/წელი
                                                                <span className="text-muted smallest fw-normal d-block">ARR მხარდაჭერა</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SECTION 2: HIGH-LEVEL METRICS / RESULTS STRIP ── */}
                    <div className="row g-3 mb-4">
                        {/* 1. Target Scope Card */}
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 h-100 bg-white border">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="smallest text-muted fw-bold">მასშტაბი (SCOPE)</span>
                                    <span className="badge bg-primary rounded-pill">{calculations.muniCount} მუნიციპ.</span>
                                </div>
                                <div className="fw-bold fs-4 text-dark">{calculations.appCount} <span className="fs-6 fw-normal text-muted">აპლიკაცია</span></div>
                                <div className="smallest text-muted mt-1">
                                    {calculations.muniCount === 1 
                                        ? selectedMunicipalities[0]?.name_ka 
                                        : `${calculations.muniCount} შერჩეული ერთეული`}
                                </div>
                            </div>
                        </div>

                        {/* 2. One-Time Setup Fee */}
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 h-100 bg-white border border-primary border-opacity-50" style={{ borderLeft: '4px solid #2563eb' }}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="smallest text-primary fw-bold">საწყისი შემოსავალი (SETUP)</span>
                                    <span className="badge bg-primary bg-opacity-10 text-primary">ერთჯერადი</span>
                                </div>
                                <div className="fw-bold fs-4 text-primary">₾{calculations.totalSetupIncome.toLocaleString()}</div>
                                <div className="smallest text-muted mt-1">
                                    ინტეგრაცია, დანერგვა, ტრენინგი
                                </div>
                            </div>
                        </div>

                        {/* 3. Annual Recurring Support (ARR) */}
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 h-100 bg-white border border-success border-opacity-50" style={{ borderLeft: '4px solid #16a34a' }}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="smallest text-success fw-bold">წლიური ARR (RECURRING)</span>
                                    <span className="badge bg-success bg-opacity-10 text-success">ყოველწლიური</span>
                                </div>
                                <div className="fw-bold fs-4 text-success">₾{calculations.totalAnnualSupport.toLocaleString()}</div>
                                <div className="smallest text-muted mt-1">
                                    ~₾{calculations.totalMonthlySupport.toLocaleString()} / თვეში (MRR)
                                </div>
                            </div>
                        </div>

                        {/* 4. 3-Year Total Contract Value (LTV) */}
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 h-100 bg-white border" style={{ borderLeft: '4px solid #7c3aed', background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.05) 0%, #ffffff 100%)' }}>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="smallest text-indigo fw-bold" style={{ color: '#7c3aed' }}>3-წლიანი ჯამური კონტრაქტი</span>
                                    <span className="badge rounded-pill text-white" style={{ background: '#7c3aed' }}>3-YEAR VALUE</span>
                                </div>
                                <div className="fw-bold fs-4" style={{ color: '#6d28d9' }}>₾{calculations.total3YearValue.toLocaleString()}</div>
                                <div className="smallest text-muted mt-1">
                                    Setup + 3 წლის მხარდაჭერის პაკეტი
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SECTION 3: VISUAL CHARTS & GRAPHS ── */}
                    <div className="row g-4 mb-4">
                        {/* CHART 1: REVENUE BREAKDOWN BY APP */}
                        <div className="col-12 col-lg-6">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                    <div className="shadcn-card-title d-flex align-items-center gap-2">
                                        <BarChart3 size={18} className="text-primary" />
                                        <span>შემოსავალი აპლიკაციების მიხედვით (₾)</span>
                                    </div>
                                    <span className="smallest text-muted">{calculations.appCount} პროდუქტი</span>
                                </div>

                                <div className="p-3">
                                    <div className="d-flex align-items-center gap-3 mb-3 small">
                                        <span className="d-flex align-items-center gap-1">
                                            <span style={{ width: '12px', height: '12px', background: '#2563eb', borderRadius: '3px', display: 'inline-block' }}></span>
                                            საწყისი Setup
                                        </span>
                                        <span className="d-flex align-items-center gap-1">
                                            <span style={{ width: '12px', height: '12px', background: '#16a34a', borderRadius: '3px', display: 'inline-block' }}></span>
                                            წლიური ARR
                                        </span>
                                    </div>

                                    {/* App Bars */}
                                    <div className="d-flex flex-column gap-3">
                                        {calculations.appBreakdown.map(app => {
                                            const maxVal = Math.max(...calculations.appBreakdown.map(a => a.appSetupTotal + a.appAnnualTotal), 1);
                                            const setupPct = ((app.appSetupTotal / maxVal) * 100);
                                            const annualPct = ((app.appAnnualTotal / maxVal) * 100);

                                            return (
                                                <div key={app.id}>
                                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                                        <span className="small fw-semibold text-dark d-flex align-items-center gap-1">
                                                            <span>{app.icon}</span>
                                                            <span>{app.name_ka}</span>
                                                        </span>
                                                        <span className="smallest fw-bold text-dark">
                                                            ₾{(app.appSetupTotal + app.appAnnualTotal).toLocaleString()} / წელ. 1
                                                        </span>
                                                    </div>

                                                    {/* Stacked Progress Bar */}
                                                    <div className="progress" style={{ height: '10px', background: '#f1f5f9' }}>
                                                        <div 
                                                            className="progress-bar" 
                                                            style={{ width: `${setupPct}%`, background: '#2563eb' }}
                                                            title={`Setup: ₾${app.appSetupTotal.toLocaleString()}`}
                                                        />
                                                        <div 
                                                            className="progress-bar" 
                                                            style={{ width: `${annualPct}%`, background: '#16a34a' }}
                                                            title={`ARR: ₾${app.appAnnualTotal.toLocaleString()}`}
                                                        />
                                                    </div>

                                                    <div className="d-flex justify-content-between smallest text-muted mt-1">
                                                        <span>Setup: ₾{app.appSetupTotal.toLocaleString()}</span>
                                                        <span>3-წლიანი წილი: {app.percentOfTotal}%</span>
                                                        <span>ARR: ₾{app.appAnnualTotal.toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CHART 2: 3-YEAR CUMULATIVE REVENUE GROWTH TRAJECTORY */}
                        <div className="col-12 col-lg-6">
                            <div className="shadcn-card h-100">
                                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                                    <div className="shadcn-card-title d-flex align-items-center gap-2">
                                        <TrendingUp size={18} className="text-success" />
                                        <span>შემოსავლის ზრდის დინამიკა (3 წელი & LTV)</span>
                                    </div>
                                    <span className="smallest text-muted">კუმულაციური ზრდა</span>
                                </div>

                                <div className="p-3">
                                    {/* SVG Area & Milestone Chart */}
                                    <div className="p-3 rounded-3 bg-light border mb-3">
                                        <svg viewBox="0 0 500 180" width="100%" height="150" style={{ overflow: 'visible' }}>
                                            <defs>
                                                <linearGradient id="revenueGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="0%" stopColor="#6366f1" stopOpacity="0.35" />
                                                    <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
                                                </linearGradient>
                                            </defs>

                                            {/* Horizontal Grid lines */}
                                            <line x1="30" y1="30" x2="480" y2="30" stroke="#e2e8f0" strokeDasharray="3,3" />
                                            <line x1="30" y1="75" x2="480" y2="75" stroke="#e2e8f0" strokeDasharray="3,3" />
                                            <line x1="30" y1="120" x2="480" y2="120" stroke="#e2e8f0" strokeDasharray="3,3" />
                                            <line x1="30" y1="150" x2="480" y2="150" stroke="#cbd5e1" strokeWidth="1.5" />

                                            {/* SVG Area */}
                                            <path 
                                                d="M 50,130 Q 150,110 250,85 T 450,30 L 450,150 L 50,150 Z" 
                                                fill="url(#revenueGrowthGrad)" 
                                            />

                                            {/* SVG Line */}
                                            <path 
                                                d="M 50,130 Q 150,110 250,85 T 450,30" 
                                                fill="none" 
                                                stroke="#4f46e5" 
                                                strokeWidth="3.5" 
                                                strokeLinecap="round"
                                            />

                                            {/* Data Points */}
                                            {/* M0: Launch */}
                                            <circle cx="50" cy="130" r="5" fill="#2563eb" stroke="#fff" strokeWidth="2" />
                                            <text x="50" y="120" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#1e3a8a">
                                                ₾{(calculations.totalSetupIncome / 1000).toFixed(0)}k
                                            </text>
                                            <text x="50" y="165" textAnchor="middle" fontSize="9" fill="#64748b">დაწყება</text>

                                            {/* Y1 */}
                                            <circle cx="170" cy="105" r="5" fill="#16a34a" stroke="#fff" strokeWidth="2" />
                                            <text x="170" y="95" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#14532d">
                                                ₾{((calculations.totalSetupIncome + calculations.totalAnnualSupport) / 1000).toFixed(0)}k
                                            </text>
                                            <text x="170" y="165" textAnchor="middle" fontSize="9" fill="#64748b">1-ლი წელი</text>

                                            {/* Y2 */}
                                            <circle cx="290" cy="75" r="5" fill="#ca8a04" stroke="#fff" strokeWidth="2" />
                                            <text x="290" y="65" textAnchor="middle" fontSize="10" fontWeight="bold" fill="#713f12">
                                                ₾{((calculations.totalSetupIncome + calculations.totalAnnualSupport * 2) / 1000).toFixed(0)}k
                                            </text>
                                            <text x="290" y="165" textAnchor="middle" fontSize="9" fill="#64748b">მე-2 წელი</text>

                                            {/* Y3 */}
                                            <circle cx="430" cy="35" r="6" fill="#7c3aed" stroke="#fff" strokeWidth="2" />
                                            <text x="430" y="25" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#581c87">
                                                ₾{(calculations.total3YearValue / 1000).toFixed(0)}k
                                            </text>
                                            <text x="430" y="165" textAnchor="middle" fontSize="9" fontWeight="bold" fill="#581c87">მე-3 წელი</text>
                                        </svg>
                                    </div>

                                    {/* Milestone Summary Cards */}
                                    <div className="row g-2">
                                        <div className="col-4">
                                            <div className="p-2 bg-white rounded border text-center">
                                                <span className="smallest text-muted d-block">1-ლი წელი</span>
                                                <span className="fw-bold small text-dark">₾{(calculations.totalSetupIncome + calculations.totalAnnualSupport).toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 bg-white rounded border text-center">
                                                <span className="smallest text-muted d-block">მე-2 წელი (+ARR)</span>
                                                <span className="fw-bold small text-dark">₾{(calculations.totalSetupIncome + calculations.totalAnnualSupport * 2).toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="p-2 bg-white rounded border text-center" style={{ background: '#f5f3ff' }}>
                                                <span className="smallest text-indigo fw-bold d-block" style={{ color: '#7c3aed' }}>3-წლიანი სრული</span>
                                                <span className="fw-bold small" style={{ color: '#7c3aed' }}>₾{calculations.total3YearValue.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── SECTION 4: DETAILED MUNICIPALITY BREAKDOWN TABLE ── */}
                    <div className="shadcn-card mb-4">
                        <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                            <div className="shadcn-card-title d-flex align-items-center gap-2">
                                <DollarSign size={18} className="text-primary" />
                                <span>შემოსავლების დეტალური განაწილება მუნიციპალიტეტების მიხედვით</span>
                            </div>
                            <span className="smallest text-muted">
                                {selectedMunicipalities.length} მუნიციპალიტეტი &times; {selectedApps.length} აპლიკაცია
                            </span>
                        </div>

                        <div className="table-responsive">
                            <table className="shadcn-table">
                                <thead>
                                    <tr>
                                        <th>მუნიციპალიტეტი</th>
                                        <th>რეგიონი</th>
                                        <th>სტატუსი</th>
                                        <th className="text-end">საწყისი Setup (₾)</th>
                                        <th className="text-end">წლიური ARR (₾)</th>
                                        <th className="text-end">3-წლიანი ჯამი (₾)</th>
                                        <th className="text-center">მოქმედება</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {calculations.muniBreakdown.map(m => {
                                        const badge = getStatusBadge(m.status);
                                        return (
                                            <tr key={m.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {m.logo ? (
                                                            <img src={m.logo} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                                        ) : (
                                                            <span>🏛️</span>
                                                        )}
                                                        <span className="fw-bold text-dark small">{m.name_ka}</span>
                                                    </div>
                                                </td>
                                                <td className="text-muted small">{m.region}</td>
                                                <td>
                                                    <span 
                                                        className="badge rounded-pill smallest fw-semibold"
                                                        style={{ background: badge.bg, color: badge.text, border: `1px solid ${badge.border}` }}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-semibold text-primary small">
                                                    ₾{m.setup.toLocaleString()}
                                                </td>
                                                <td className="text-end fw-semibold text-success small">
                                                    ₾{m.annual.toLocaleString()}
                                                </td>
                                                <td className="text-end fw-bold text-dark small">
                                                    ₾{m.total3Yr.toLocaleString()}
                                                </td>
                                                <td className="text-center">
                                                    <Link 
                                                        to={`/admin/municipalities/${m.id}`}
                                                        className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                                        style={{ fontSize: '11px', padding: '2px 8px' }}
                                                    >
                                                        დეტალები <ChevronRight size={12} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {calculations.muniBreakdown.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4 text-muted">
                                                მუნიციპალიტეტი არ არის შერჩეული. გთხოვთ აირჩიოთ ზედა სიიდან.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {calculations.muniBreakdown.length > 0 && (
                                    <tfoot className="table-light">
                                        <tr className="fw-bold">
                                            <td colSpan="3" className="text-end text-dark">ჯამი:</td>
                                            <td className="text-end text-primary">₾{calculations.totalSetupIncome.toLocaleString()}</td>
                                            <td className="text-end text-success">₾{calculations.totalAnnualSupport.toLocaleString()}</td>
                                            <td className="text-end text-dark fs-6">₾{calculations.total3YearValue.toLocaleString()}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                /* ══════════════════════════════════════════════════════════════════════════════
                   VIEW 2: FULL MUNICIPAL REPORT (STATUS & DIGITAL INFRASTRUCTURE AUDIT)
                   ══════════════════════════════════════════════════════════════════════════════ */
                <div>
                    {/* Nationwide Summary Statistics */}
                    <div className="row g-3 mb-4">
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 bg-white border">
                                <span className="smallest text-muted fw-bold d-block">სრული მუნიციპალიტეტები</span>
                                <span className="fw-bold fs-3 text-dark">{municipalities.length}</span>
                                <span className="smallest text-muted d-block mt-1">საქართველოს არაოკუპირებული ერთეულები</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 bg-white border" style={{ borderLeft: '4px solid #16a34a' }}>
                                <span className="smallest text-success fw-bold d-block">კონტრაქტი / ლაივ მხარდაჭერა</span>
                                <span className="fw-bold fs-3 text-success">
                                    {municipalities.filter(m => ['contract_signed', 'supported_live'].includes(m.status)).length}
                                </span>
                                <span className="smallest text-muted d-block mt-1">აქტიური პარტნიორები</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 bg-white border" style={{ borderLeft: '4px solid #ca8a04' }}>
                                <span className="smallest text-warning fw-bold d-block" style={{ color: '#ca8a04' }}>სამუშაო პროცესში</span>
                                <span className="fw-bold fs-3" style={{ color: '#ca8a04' }}>
                                    {municipalities.filter(m => ['negotiation', 'meeting_scheduled', 'in_development'].includes(m.status)).length}
                                </span>
                                <span className="smallest text-muted d-block mt-1">მოლაპარაკება & შეხვედრები</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="shadcn-card p-3 bg-white border" style={{ borderLeft: '4px solid #2563eb' }}>
                                <span className="smallest text-primary fw-bold d-block">შეთავაზება გაგზავნილია</span>
                                <span className="fw-bold fs-3 text-primary">
                                    {municipalities.filter(m => m.status === 'offer_sent').length}
                                </span>
                                <span className="smallest text-muted d-block mt-1">პასუხის მოლოდინი</span>
                            </div>
                        </div>
                    </div>

                    {/* Filter and Search Bar for Report */}
                    <div className="shadcn-card mb-4">
                        <div className="p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                            <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ maxWidth: '400px' }}>
                                <div className="position-relative w-100">
                                    <Search size={14} className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" />
                                    <input 
                                        type="text" 
                                        className="shadcn-input ps-4" 
                                        placeholder="მუნიციპალიტეტის ძებნა..."
                                        value={reportSearch}
                                        onChange={e => setReportSearch(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="smallest text-muted fw-bold">სტატუსის ფილტრი:</span>
                                <button 
                                    className={`btn btn-sm ${reportFilterStatus === 'all' ? 'btn-dark' : 'btn-outline-secondary'}`}
                                    onClick={() => setReportFilterStatus('all')}
                                    style={{ fontSize: '11px' }}
                                >
                                    ყველა ({municipalities.length})
                                </button>
                                <button 
                                    className={`btn btn-sm ${reportFilterStatus === 'green' ? 'btn-success' : 'btn-outline-success'}`}
                                    onClick={() => setReportFilterStatus('green')}
                                    style={{ fontSize: '11px' }}
                                >
                                    🟢 კონტრაქტი
                                </button>
                                <button 
                                    className={`btn btn-sm ${reportFilterStatus === 'yellow' ? 'btn-warning' : 'btn-outline-warning'}`}
                                    onClick={() => setReportFilterStatus('yellow')}
                                    style={{ fontSize: '11px' }}
                                >
                                    🟡 პროცესში
                                </button>
                                <button 
                                    className={`btn btn-sm ${reportFilterStatus === 'blue' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setReportFilterStatus('blue')}
                                    style={{ fontSize: '11px' }}
                                >
                                    🔵 შეთავაზება
                                </button>
                                <button 
                                    className={`btn btn-sm ${reportFilterStatus === 'red' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => setReportFilterStatus('red')}
                                    style={{ fontSize: '11px' }}
                                >
                                    🔴 ლიდები
                                </button>
                            </div>
                        </div>

                        {/* Full Municipalities Table */}
                        <div className="table-responsive">
                            <table className="shadcn-table">
                                <thead>
                                    <tr>
                                        <th>მუნიციპალიტეტი</th>
                                        <th>რეგიონი</th>
                                        <th>ოფიციალური ვებ-მისამართი</th>
                                        <th>CRM სტატუსი</th>
                                        <th className="text-end">სავარაუდო ბიუჯეტი</th>
                                        <th className="text-center">მოქმედება</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportMunicipalities.map(m => {
                                        const badge = getStatusBadge(m.status);
                                        return (
                                            <tr key={m.id}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {m.logo ? (
                                                            <img src={m.logo} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                                                        ) : (
                                                            <span>🏛️</span>
                                                        )}
                                                        <div>
                                                            <div className="fw-bold text-dark small">{m.name_ka}</div>
                                                            <div className="text-muted smallest">{m.name_en}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="small text-muted">{m.region_ka || m.region}</td>
                                                <td>
                                                    {m.official_website ? (
                                                        <a 
                                                            href={m.official_website} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer" 
                                                            className="small text-primary d-flex align-items-center gap-1 text-decoration-none"
                                                        >
                                                            <Globe size={12} />
                                                            <span>{m.official_website.replace(/^https?:\/\//, '')}</span>
                                                            <ExternalLink size={10} />
                                                        </a>
                                                    ) : (
                                                        <span className="text-muted smallest">ვებსაიტი არ არის</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span 
                                                        className="badge rounded-pill smallest fw-semibold"
                                                        style={{ background: badge.bg, color: badge.text, border: `1px solid ${badge.border}` }}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                </td>
                                                <td className="text-end fw-semibold text-dark small">
                                                    ₾{parseFloat(m.estimated_budget || 0).toLocaleString()}
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <button 
                                                            className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm"
                                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                                            onClick={() => {
                                                                setSelectedMunIds([m.id]);
                                                                setActiveView('calculator');
                                                            }}
                                                            title="კალკულატორში გახსნა"
                                                        >
                                                            <Calculator size={11} /> კალკულატორი
                                                        </button>
                                                        <Link 
                                                            to={`/admin/municipalities/${m.id}`}
                                                            className="shadcn-btn shadcn-btn-primary shadcn-btn-sm"
                                                            style={{ fontSize: '11px', padding: '2px 8px' }}
                                                        >
                                                            პროფილი <ChevronRight size={11} />
                                                        </Link>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminMunicipalityReport;
